import axios from 'axios';
import Cookies from 'js-cookie';
import { sessionExpired } from '../comp/login/authReducer';

axios.defaults.baseURL = '/';
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  const csrfToken = Cookies.get('csrftoken');
  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

const Api = {
  // Accounts
  fetchAccounts: () => axios.get('api/v1/accounts/'),
  fetchAccount: (id) => axios.get(`api/v1/accounts/${id}/`),
  createAccount: (data) => axios.post('api/v1/accounts/', data),
  updateAccount: (id, data) => axios.patch(`api/v1/accounts/${id}/`, data),
  deleteAccount: (id) => axios.delete(`api/v1/accounts/${id}/`),

  // Scans
  fetchScans: (params) => axios.get('api/v1/scans/', { params }),
  fetchScan: (correlationId) => axios.get(`api/v1/scans/${correlationId}/`),

  // Dashboard stats
  fetchDashboardStats: () => axios.get('api/v1/dashboard/stats/'),
  fetchRecentScans: () => axios.get('api/v1/dashboard/recent-scans/'),
  fetchDashboardTimeseries: (days = 7) =>
    axios.get('api/v1/dashboard/timeseries/', { params: { days } }),
  fetchDashboardBreakdowns: (days = 7) =>
    axios.get('api/v1/dashboard/breakdowns/', { params: { days } }),

  // Settings / API Keys
  fetchApiKeys: () => axios.get('api/v1/settings/api-keys/'),
  regenerateApiKey: (accountId) => axios.post(`api/v1/settings/api-keys/${accountId}/regenerate/`),
  createApiKey: (data) => axios.post('api/v1/settings/api-keys/create/', data),
};

export default Api;

// Track the interceptor ID so re-calling installSessionExpiredInterceptor
// (e.g. in tests) replaces rather than stacks the interceptor.
let _sessionExpiredInterceptorId = null;

/**
 * Attach a global axios response interceptor that catches 401 responses,
 * dispatches sessionExpired() to clear Redux auth state, and redirects the
 * user to /login?reason=session_expired.
 *
 * Idempotent: calling this again ejects the previous interceptor before
 * installing a new one, preventing interceptor accumulation in tests.
 *
 * The window.__sessionExpiredRedirect flag ensures parallel 401 responses
 * (e.g. two simultaneous API calls expiring together) only produce one redirect.
 * The flag is cleared on the next successful session check (see authReducer.js
 * checkSessionSaga), so a future session expiry will redirect again.
 *
 * @param {object} store  - Redux store (must have .dispatch)
 * @param {object} history - History object (must have .replace)
 */
export function installSessionExpiredInterceptor(store, history) {
  if (_sessionExpiredInterceptorId !== null) {
    axios.interceptors.response.eject(_sessionExpiredInterceptorId);
  }

  _sessionExpiredInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const path = window.location.pathname;
      if (status === 401 && !path.startsWith('/login')) {
        if (!window.__sessionExpiredRedirect) {
          window.__sessionExpiredRedirect = true;
          store.dispatch(sessionExpired());
          history.replace('/login?reason=session_expired');
        }
      }
      return Promise.reject(error);
    },
  );
}
